import { useMemo } from "react";
import { useCalendars } from "./useTemplates";
import { useCalendarOverrides } from "./useCalendarOverrides";
import { makeIsNonWorkingDay } from "@/lib/calendar-non-working";

/**
 * Returns a predicate `(date) => isNonWorking` for the given calendar id.
 * Loads the calendar list (cheap, cached) + its overrides. While data is
 * still loading, the predicate uses the default Mon–Fri week.
 *
 * `null` → returns predicate that uses pure default week — useful when
 * the caller doesn't yet know the calendar (e.g. project still loading).
 */
export function useIsNonWorkingDay(calendarId: number | null): (date: Date) => boolean {
  const calendarsQuery = useCalendars();
  const overridesQuery = useCalendarOverrides(calendarId);

  const weekly = useMemo(() => {
    if (calendarId === null) return null;
    const cal = (calendarsQuery.data ?? []).find((c) => c.id === calendarId);
    return cal?.weekly_pattern ?? null;
  }, [calendarsQuery.data, calendarId]);

  return useMemo(
    () => makeIsNonWorkingDay(weekly, overridesQuery.data ?? []),
    [weekly, overridesQuery.data],
  );
}
